import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";


function AddSlider() {

    const navigate = useNavigate();


    /*
    |--------------------------------------------------------------------------
    | Form Data
    |--------------------------------------------------------------------------
    */

    const [image, setImage] = useState(null);

    const [badge, setBadge] = useState("");

    const [title, setTitle] = useState("");

    const [description, setDescription] = useState("");

    const [order, setOrder] = useState("0");


    /*
    |--------------------------------------------------------------------------
    | Loading State
    |--------------------------------------------------------------------------
    */

    const [saving, setSaving] = useState(false);


    /*
    |--------------------------------------------------------------------------
    | Handle Image Change
    |--------------------------------------------------------------------------
    */

    const handleImageChange = (e) => {

        const file = e.target.files[0];

        setImage(file || null);
    };


    /*
    |--------------------------------------------------------------------------
    | Submit Form
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (e) => {

        e.preventDefault();


        /*
        |--------------------------------------------------------------------------
        | Basic Validation
        |--------------------------------------------------------------------------
        */

        if (!image) {

            Swal.fire({

                icon: "warning",

                title: "Image Required",

                text:
                    "Please select slider image."

            });

            return;
        }


        if (!badge.trim()) {

            Swal.fire({

                icon: "warning",

                title: "Badge Required",

                text:
                    "Please enter slider badge."

            });

            return;
        }


        if (!title.trim()) {

            Swal.fire({

                icon: "warning",

                title: "Title Required",

                text:
                    "Please enter slider title."

            });

            return;
        }


        if (!description.trim()) {

            Swal.fire({

                icon: "warning",

                title: "Description Required",

                text:
                    "Please enter slider description."

            });

            return;
        }


        try {

            setSaving(true);


            /*
            |--------------------------------------------------------------------------
            | FormData
            |--------------------------------------------------------------------------
            */

            const formData = new FormData();


            formData.append(
                "image",
                image
            );


            formData.append(
                "badge",
                badge.trim()
            );


            formData.append(
                "title",
                title.trim()
            );


            formData.append(
                "description",
                description.trim()
            );


            formData.append(
                "order",
                order
            );


            /*
            |--------------------------------------------------------------------------
            | API Request
            |--------------------------------------------------------------------------
            */

            const response = await fetch(

                `${API_BASE_URL}/admin/sliders`,

                {

                    method: "POST",

                    headers: {

                        "Accept":
                            "application/json"

                    },

                    credentials:
                        "include",

                    body: formData

                }

            );


            /*
            |--------------------------------------------------------------------------
            | Response
            |--------------------------------------------------------------------------
            */

            const data =
                await response.json();


            /*
            |--------------------------------------------------------------------------
            | Success
            |--------------------------------------------------------------------------
            */

            if (
                response.ok &&
                data.success
            ) {

                await Swal.fire({

                    icon: "success",

                    title: "Success!",

                    text:
                        data.message ||
                        "Slider added successfully.",

                    timer: 1500,

                    showConfirmButton:
                        false

                });


                navigate("/sliders");

            }


            /*
            |--------------------------------------------------------------------------
            | API Error
            |--------------------------------------------------------------------------
            */

            else {

                let errorMessage =
                    data.message ||
                    "Something went wrong.";


                /*
                |--------------------------------------------------------------------------
                | Validation Errors
                |--------------------------------------------------------------------------
                */

                if (data.errors) {

                    const validationErrors =
                        Object.values(
                            data.errors
                        ).flat();


                    if (
                        validationErrors.length >
                        0
                    ) {

                        errorMessage =
                            validationErrors.join(
                                "\n"
                            );

                    }

                }


                Swal.fire({

                    icon: "error",

                    title:
                        "Unable to Add Slider",

                    text:
                        errorMessage

                });

            }

        } catch (error) {

            console.error(
                "Add Slider Error:",
                error
            );


            Swal.fire({

                icon: "error",

                title:
                    "Server Error",

                text:
                    "Unable to connect with server."

            });

        } finally {

            setSaving(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (

        <div className="container-fluid py-4" style={{
                            maxHeight: "500px",
                            overflowX: "auto",
                            overflowY: "auto"
                        }}>


            {/* ---------------------------------------------------------------- */}
            {/* Header */}
            {/* ---------------------------------------------------------------- */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mb-4
                "
            >

                <h3 className="mb-0">
                    Add Slider
                </h3>


                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() =>
                        navigate("/sliders")
                    }
                    disabled={saving}
                >
                    Back
                </button>

            </div>


            {/* ---------------------------------------------------------------- */}
            {/* Form Card */}
            {/* ---------------------------------------------------------------- */}

            <div className="card shadow-sm">

                <div className="card-body">

                    <form
                        onSubmit={handleSubmit}
                    >


                        {/* ---------------------------------------------------- */}
                        {/* Image */}
                        {/* ---------------------------------------------------- */}

                        <div className="mb-3">

                            <label
                                className="form-label"
                            >
                                Slider Image
                                <span className="text-danger">
                                    {" "}*
                                </span>
                            </label>


                            <input
                                type="file"
                                className="form-control"
                                accept="
                                    image/jpeg,
                                    image/jpg,
                                    image/png,
                                    image/webp
                                "
                                onChange={
                                    handleImageChange
                                }
                                disabled={saving}
                            />


                            <small
                                className="
                                    text-muted
                                "
                            >
                                Recommended slider image
                                size: 1920 × 600 px.
                                Maximum file size: 5 MB.
                            </small>

                        </div>


                        {/* ---------------------------------------------------- */}
                        {/* Badge */}
                        {/* ---------------------------------------------------- */}

                        <div className="mb-3">

                            <label
                                className="form-label"
                            >
                                Badge
                                <span className="text-danger">
                                    {" "}*
                                </span>
                            </label>


                            <input
                                type="text"
                                className="form-control"
                                placeholder="
                                    Enter slider badge
                                "
                                value={badge}
                                maxLength={100}
                                onChange={(e) =>
                                    setBadge(
                                        e.target.value
                                    )
                                }
                                disabled={saving}
                            />


                            <small
                                className="
                                    text-muted
                                "
                            >
                                Example: NEET PG 2026
                            </small>

                        </div>


                        {/* ---------------------------------------------------- */}
                        {/* Title */}
                        {/* ---------------------------------------------------- */}

                        <div className="mb-3">

                            <label
                                className="form-label"
                            >
                                Title
                                <span className="text-danger">
                                    {" "}*
                                </span>
                            </label>


                            <input
                                type="text"
                                className="form-control"
                                placeholder="
                                    Enter slider title
                                "
                                value={title}
                                maxLength={255}
                                onChange={(e) =>
                                    setTitle(
                                        e.target.value
                                    )
                                }
                                disabled={saving}
                            />


                            <small
                                className="
                                    text-muted
                                "
                            >
                                Example: India's Best NEET PG
                                Preparation Courses
                            </small>

                        </div>


                        {/* ---------------------------------------------------- */}
                        {/* Description */}
                        {/* ---------------------------------------------------- */}

                        <div className="mb-3">

                            <label
                                className="form-label"
                            >
                                Description
                                <span className="text-danger">
                                    {" "}*
                                </span>
                            </label>


                            <textarea
                                className="form-control"
                                rows="5"
                                placeholder="
                                    Enter slider description
                                "
                                value={description}
                                onChange={(e) =>
                                    setDescription(
                                        e.target.value
                                    )
                                }
                                disabled={saving}
                            />


                            <small
                                className="
                                    text-muted
                                "
                            >
                                Enter a short description
                                for this slider.
                            </small>

                        </div>


                        {/* ---------------------------------------------------- */}
                        {/* Order */}
                        {/* ---------------------------------------------------- */}

                        <div className="mb-3">

                            <label
                                className="form-label"
                            >
                                Order
                                <span className="text-danger">
                                    {" "}*
                                </span>
                            </label>


                            <input
                                type="number"
                                className="form-control"
                                min="0"
                                value={order}
                                onChange={(e) =>
                                    setOrder(
                                        e.target.value
                                    )
                                }
                                disabled={saving}
                            />


                            <small
                                className="
                                    text-muted
                                "
                            >
                                Lower number sliders will
                                appear first.
                            </small>

                        </div>


                        {/* ---------------------------------------------------- */}
                        {/* Buttons */}
                        {/* ---------------------------------------------------- */}

                        <div
                            className="
                                d-flex
                                gap-2
                            "
                        >

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={saving}
                            >

                                {saving ? (

                                    <>
                                        <span
                                            className="
                                                spinner-border
                                                spinner-border-sm
                                                me-2
                                            "
                                            role="status"
                                            aria-hidden="true"
                                        />

                                        Saving...

                                    </>

                                ) : (

                                    "Save Slider"

                                )}

                            </button>


                            <button
                                type="button"
                                className="btn btn-secondary"
                                disabled={saving}
                                onClick={() =>
                                    navigate("/sliders")
                                }
                            >
                                Cancel
                            </button>

                        </div>


                    </form>

                </div>

            </div>

        </div>

    );

}


export default AddSlider;